// ABOUTME: Hook for monitoring network connectivity status
// ABOUTME: Integrates with SyncService to provide real-time online/offline state

import { useState, useEffect, useCallback } from 'react'
import { getSyncService, type SyncStatus } from '@/services/SyncService'

export interface NetworkStatus {
  online: boolean
  syncing: boolean
  lastSync?: Date
  pendingOperations: number
  failedOperations: number
}

/**
 * Hook to monitor network status and sync state
 * Provides reactive updates when network status changes
 */
export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>(() => {
    const syncService = getSyncService()
    return syncService.getStatus()
  })

  useEffect(() => {
    const syncService = getSyncService()

    // Subscribe to sync service status changes
    const unsubscribe = syncService.onStatusChange((newStatus: SyncStatus) => {
      setStatus(newStatus)
    })

    // Also listen to browser online/offline events for immediate feedback
    const handleOnline = () => {
      setStatus((prev) => ({ ...prev, online: true }))
    }

    const handleOffline = () => {
      setStatus((prev) => ({ ...prev, online: false }))
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      unsubscribe()
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return status
}

/**
 * Hook for triggering manual sync
 * Returns a function to trigger sync and loading state
 */
export function useSync(): {
  sync: () => Promise<void>
  syncing: boolean
} {
  const [syncing, setSyncing] = useState(false)

  const sync = useCallback(async () => {
    setSyncing(true)
    try {
      const syncService = getSyncService()
      await syncService.sync()
    } finally {
      setSyncing(false)
    }
  }, [])

  return { sync, syncing }
}
